// src/contexts/WalletContext.jsx (Phantom/Solana)
import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import { connectWalletAndGetProfile } from '../services/agentService';

const WalletContext = createContext(undefined);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  const [currentAccount, setCurrentAccount] = useState(null); // base58 public key string
  const [provider, setProvider] = useState(null); // Phantom provider (window.solana)
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  const clearError = useCallback(() => setError(""), []);

  const setAccount = useCallback(async (publicKey) => {
    if (publicKey) {
      const address = typeof publicKey === 'string' ? publicKey : publicKey.toString();
      setCurrentAccount(address);
      try {
        const profile = await connectWalletAndGetProfile(address, null);
        setUserProfile(profile);
        setError("");
      } catch (profileError) {
        console.error("WalletContext(Solana): profile sync error", profileError);
      }
    } else {
      setCurrentAccount(null);
      setUserProfile(null);
    }
  }, []);

  // Initialize Phantom provider and listeners
  useEffect(() => {
    let isMounted = true;
    const solana = typeof window !== 'undefined' ? window.solana : undefined;
    if (solana && solana.isPhantom) {
      if (isMounted) setProvider(solana);

      // Try silent connect if already trusted
      solana.connect({ onlyIfTrusted: true })
        .then(({ publicKey }) => {
          if (isMounted && publicKey) setAccount(publicKey);
        })
        .catch(() => {/* ignore silent connect errors */});

      const onConnect = (publicKey) => {
        if (isMounted) setAccount(publicKey);
      };
      const onAccountChanged = (publicKey) => {
        if (!isMounted) return;
        if (publicKey) setAccount(publicKey); else setAccount(null);
      };
      const onDisconnect = () => {
        if (isMounted) setAccount(null);
      };

      solana.on('connect', onConnect);
      // Phantom currently emits 'accountChanged' for account switches
      solana.on('accountChanged', onAccountChanged);
      solana.on('disconnect', onDisconnect);

      return () => {
        isMounted = false;
        try {
          solana.removeListener && solana.removeListener('connect', onConnect);
          solana.removeListener && solana.removeListener('accountChanged', onAccountChanged);
          solana.removeListener && solana.removeListener('disconnect', onDisconnect);
        } catch {}
      };
    } else {
      if (isMounted) setError("Phantom Wallet not detected. Please install Phantom to use this app.");
    }
    return () => { isMounted = false; };
  }, [setAccount]);

  const connectWallet = useCallback(async () => {
    const solana = typeof window !== 'undefined' ? window.solana : undefined;
    if (!solana || !solana.isPhantom) {
      setError("Phantom Wallet not detected. Please install Phantom.");
      return;
    }
    setIsLoading(true);
    clearError();
    try {
      const { publicKey } = await solana.connect();
      await setAccount(publicKey);
    } catch (err) {
      console.error("WalletContext(Solana): connect error", err);
      if (err?.code === 4001) {
        setError("Connection request rejected by user.");
      } else {
        setError("Failed to connect wallet: " + (err?.message || 'Unknown error'));
      }
      setCurrentAccount(null);
      setUserProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, [setAccount, clearError]);

  const disconnectWallet = useCallback(async () => {
    const solana = typeof window !== 'undefined' ? window.solana : undefined;
    try {
      await solana?.disconnect();
    } catch {}
    setAccount(null);
  }, [setAccount]);

  const signMessage = useCallback(async (message) => {
    const solana = provider;
    if (!solana || !currentAccount) {
      setError("Wallet not connected.");
      return null;
    }
    try {
      const encoded = new TextEncoder().encode(typeof message === 'string' ? message : JSON.stringify(message));
      const { signature, publicKey } = await solana.signMessage(encoded, 'utf8');
      return { signature, publicKey: publicKey?.toString?.() };
    } catch (err) {
      console.error("WalletContext(Solana): signMessage error", err);
      setError("Failed to sign message: " + (err?.message || 'Unknown error'));
      return null;
    }
  }, [provider, currentAccount]);

  const value = {
    currentAccount,
    provider,
    signer: null, // no EVM signer in Solana
    userProfile,
    error,
    isLoading,
    connectWallet,
    disconnectWallet,
    signMessage,
    clearError,
    setError,
  };

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
};